import {IAutoOrderOrder} from '../../auto-ordering/models';
import {Ascii} from '../../../shared/shared-models/global/ascii.enum';
import {
  AutoOrderOrderErrorHumanReadableEnum,
} from '../../auto-ordering/models/auto-order-order-error.enum';
import {
  AutoOrderNotificationItemErrorReasonEnum,
  IAutoOrderNotificationItemError,
  IAutoOrderNotificationItemErrorEntry,
  IAutoOrderNotificationItemErrorGuidance,
} from '../models/notification-auto-order-item-error.model';
import {INotification, NotificationResponseTypeEnum} from '../models/notification';

const AUTO_ORDER_ITEM_ERROR_GUIDANCE: Record<string, string> = {
  [AutoOrderNotificationItemErrorReasonEnum.itemOnHold]:
    'Remove these items and retry, or remove the items from being on hold for purchase orders before retrying.',
  [AutoOrderNotificationItemErrorReasonEnum.itemNotFound]:
    'Remove these items — they are no longer in the stock file.',
  [AutoOrderNotificationItemErrorReasonEnum.iqProcessingError]:
    'Check these items in IQ (supplier link, on-hold status, master data), then remove or fix and retry.',
  [AutoOrderNotificationItemErrorReasonEnum.unknownError]:
    'Remove affected items and retry, or contact support.',
};

/**
 * Returns user-facing guidance for an auto-order item error reason.
 */
export function getAutoOrderItemErrorGuidance(
  reason: AutoOrderNotificationItemErrorReasonEnum | string | undefined,
): string {
  if (!reason) {
    return AUTO_ORDER_ITEM_ERROR_GUIDANCE[AutoOrderNotificationItemErrorReasonEnum.unknownError];
  }
  return AUTO_ORDER_ITEM_ERROR_GUIDANCE[reason]
    ?? AUTO_ORDER_ITEM_ERROR_GUIDANCE[AutoOrderNotificationItemErrorReasonEnum.unknownError];
}

/**
 * Whether the notification represents a failed auto-order order response.
 */
export function isAutoOrderOrderResponseFailure(notification: INotification | undefined): boolean {
  if (!notification || notification.success) {
    return false;
  }
  return notification.responseType === NotificationResponseTypeEnum.orderResponse
    || notification.type === 'AUTO_ORDERS';
}

/**
 * Resolves itemErrors from the notification and/or loaded order document.
 */
export function resolveAutoOrderNotificationItemErrors(
  notification: INotification | undefined,
  order?: IAutoOrderOrder,
): Record<string, IAutoOrderNotificationItemError> | null {
  if (notification?.itemErrors && Object.keys(notification.itemErrors).length > 0) {
    return notification.itemErrors;
  }
  const orderRootItemErrors = (order as IAutoOrderOrder & {
    itemErrors?: Record<string, IAutoOrderNotificationItemError> | null;
  })?.itemErrors;
  if (orderRootItemErrors && Object.keys(orderRootItemErrors).length > 0) {
    return orderRootItemErrors;
  }
  const nestedItemErrors = order?.error?.itemErrors;
  if (nestedItemErrors && Object.keys(nestedItemErrors).length > 0) {
    return Object.fromEntries(
      Object.entries(nestedItemErrors).map(([stockCode, itemError]) => [
        stockCode,
        {
          desc: itemError.desc,
          error: itemError.error as number,
          reason: mapLegacyItemErrorToReason(itemError.error as number),
        },
      ]),
    );
  }
  return null;
}

/**
 * Builds display rows for each failing stock item.
 */
export function buildAutoOrderNotificationItemErrorEntries(
  itemErrors: Record<string, IAutoOrderNotificationItemError> | null,
): IAutoOrderNotificationItemErrorEntry[] {
  if (!itemErrors) {
    return [];
  }
  return Object.entries(itemErrors).map(([stockCode, itemError]: [string, IAutoOrderNotificationItemError]) => ({
    stockCode,
    desc: itemError.desc,
    reason: itemError.reason,
    guidance: getAutoOrderItemErrorGuidance(itemError.reason),
  }));
}

/**
 * Returns unique guidance messages for the failing items grouped by reason.
 */
export function buildAutoOrderNotificationItemErrorGuidance(
  entries: IAutoOrderNotificationItemErrorEntry[],
): IAutoOrderNotificationItemErrorGuidance[] {
  const seenReasons = new Set<string>();
  const guidance: IAutoOrderNotificationItemErrorGuidance[] = [];
  entries.forEach((entry: IAutoOrderNotificationItemErrorEntry) => {
    const reasonKey: string = entry.reason || AutoOrderNotificationItemErrorReasonEnum.unknownError;
    if (seenReasons.has(reasonKey)) {
      return;
    }
    seenReasons.add(reasonKey);
    guidance.push({
      reason: entry.reason,
      guidance: entry.guidance,
    });
  });
  return guidance;
}

/**
 * Builds modal description lines for a failed auto-order notification.
 */
export function buildAutoOrderNotificationErrorDescription(
  notification: INotification | undefined,
  order?: IAutoOrderOrder,
): string[] {
  const lines: string[] = [];
  const summary: string | undefined = notification?.response;
  if (summary) {
    lines.push(summary);
    lines.push(Ascii.EmptySpace);
  } else if (order?.error) {
    if (typeof order.error === 'string') {
      lines.push(order.error);
    } else {
      lines.push(`${AutoOrderOrderErrorHumanReadableEnum[order.error.error]} - ${order.error.desc}`);
    }
    lines.push(Ascii.EmptySpace);
  } else if (order?.errorMsg) {
    if (typeof order.errorMsg === 'string') {
      lines.push(order.errorMsg);
    } else {
      if (order.errorMsg.message) {
        lines.push(order.errorMsg.message);
      }
      if (order.errorMsg.code) {
        lines.push(`Error: ${order.errorMsg.code}`);
      }
    }
    lines.push(Ascii.EmptySpace);
  }
  const itemErrors: Record<string, IAutoOrderNotificationItemError> | null =
    resolveAutoOrderNotificationItemErrors(notification, order);
  const entries: IAutoOrderNotificationItemErrorEntry[] =
    buildAutoOrderNotificationItemErrorEntries(itemErrors);
  if (entries.length > 0) {
    lines.push('The following items could not be processed:');
    lines.push(Ascii.EmptySpace);
    entries.forEach((entry: IAutoOrderNotificationItemErrorEntry) => {
      lines.push(`${entry.stockCode} — ${entry.desc}`);
    });
    lines.push(Ascii.EmptySpace);
    buildAutoOrderNotificationItemErrorGuidance(entries).forEach(
      (guidanceEntry: IAutoOrderNotificationItemErrorGuidance) => {
        lines.push(guidanceEntry.guidance);
        lines.push(Ascii.EmptySpace);
      },
    );
  }
  return lines;
}

/**
 * Maps legacy numeric item error codes to notification reason strings.
 */
function mapLegacyItemErrorToReason(errorCode: number): AutoOrderNotificationItemErrorReasonEnum {
  switch (errorCode) {
    case -1:
      return AutoOrderNotificationItemErrorReasonEnum.itemOnHold;
    case -2:
      return AutoOrderNotificationItemErrorReasonEnum.itemNotFound;
    default:
      return AutoOrderNotificationItemErrorReasonEnum.unknownError;
  }
}
